import Button from 'components/Form/Button';
import Check from 'components/Form/Check';
import BasicTable from 'components/Form/Table';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import moment from 'moment';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Read } from 'services/financing-desk-controls';
import { exposeStrapiError, formatarValor, marcadorDiferente } from 'utils';
import { formatadores, normalizeName } from 'utils/validators';

export default function ControlDeskConnection() {
  const { user, filterExpression, setModal } = useContext(CoreContext);
  const [registers, setRegisters] = useState([]);
  const columns = useMemo(() => {
    // Define as colunas padrão
    const defaultColumns = [
      {
        title: 'Id',
        sortable: true,
        ref: 'proposta',
        renderCell: ({ row }) => {
          // Certifique-se de que os campos estão no lugar correto
          const isAnalisadoCuradoria =
            row?.proposta_financeira?.curadoria_analise === 'Analisado';
          const isAnalisadoBot =
            row?.proposta_financeira?.curadoria_analise_bot === 'Analisado';
          // Pega o ID da proposta ou fallback para row.proposta
          const propostaId = row?.proposta_financeira?.id || row?.proposta;
          if (isAnalisadoCuradoria || isAnalisadoBot) {
            return marcadorDiferente(propostaId, '!');
          }
          return propostaId;
        },
      },
      {
        title: 'Data Contato',
        sortable: true,
        ref: 'dataContato',
        align: 'center',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row?.dataContato),
      },
      { title: 'Proposta', ref: 'cod_proposta_externa' },
      ...(user?.administrador ||
      (user?.distribuidores && user.distribuidores.length >= 1)
        ? [
            {
              title: 'Distribuidor',
              sortable: true,
              ref: 'distributor',
            },
          ]
        : []),
      { title: 'Integrador', sortable: true, ref: 'integrator' },
      { title: 'Cliente', sortable: true, ref: 'cliente' },
      { title: 'Cidade', ref: 'cidade' },
      { title: 'UF', ref: 'uf' },
      { title: 'Concessionária', ref: 'concessionaria' },
      { title: 'KWP', ref: 'kwp' },
      {
        title: 'Conexão',
        ref: '',
        align: 'center',
        renderCell: ({ row }) => (
          <Check
            checked={row?.conexao === 'Sim' ? true : false}
            label={''}
            align="center"
          />
        ),
      },
      {
        title: '1° Parcela',
        ref: '',
        renderCell: ({ row }) => (
          <Check
            checked={row?.primeiro_pagamento === 'Sim' ? true : false}
            label={''}
            align="center"
          />
        ),
      },
      ...(user?.administrador
        ? [
            {
              title: 'Observações',
              renderCell: ({ row }) => (
                <Button
                  onClick={() => {
                    setModal({
                      type: 'observacao',
                      dados: row,
                      origem: 'Conexão e Primeira Parcela',
                      reload: () => init(),
                    });
                  }}
                  color="botao"
                  nospace
                  mbutton
                  small
                >
                  Adicionar
                </Button>
              ),
            },
          ]
        : []),
    ];
    return defaultColumns.filter((f) => f);
  }, [user, setModal]);

  const rows = useMemo(() => {
    return registers
      ?.filter((item) => !(item.conexao && item?.primeiro_pagamento))
      .map((item) => ({
        proposta: item?.proposta_financeira?.id,
        id: item?.id,
        original: item,
        data: item?.proposta_financeira?.createdAt,
        formatedDate: formatadores.TIMESTAMP_TO_BR(
          item?.proposta_financeira?.createdAt
        ),
        cliente: normalizeName(
          item?.proposta_financeira?.informacoes_cliente?.nome_completo
        ),
        //usado no filtro:
        distributor: normalizeName(
          item?.proposta_financeira?.distribuidor?.trade_name
        ),
        integrator: normalizeName(
          item?.proposta_financeira?.integrador?.nome_fantasia
        ),
        uf: item?.proposta_financeira?.endereco_instalacao?.uf,
        status: normalizeName(item?.proposta_financeira?.status),
        //fim do filtro.
        cidade: normalizeName(
          item?.proposta_financeira?.endereco_instalacao?.cidade
        ),
        concessionaria: normalizeName(
          item?.proposta_financeira?.informacoes_projeto?.concessionaria
        ),
        kwp: formatarValor(
          item?.proposta_financeira?.informacoes_projeto?.potencia_kWp
        ),
        conhecimento_compra: item?.conhecimento_compra,
        cod_proposta_externa: normalizeName(
          item?.proposta_financeira?.cod_proposta_externa
        ),
        primeiro_pagamento: item?.primeiro_pagamento ? 'Sim' : 'Não',
        conexao: item?.conexao ? 'Sim' : 'Não',
        dataContato: item?.data_entrada_conexao || item?.data_enviar_conexao,
        formatedDateContact: formatadores.TIMESTAMP_TO_BR(
          item?.data_entrada_conexao || item?.data_enviar_conexao
        ),
        curadoria_analise: item?.proposta_financeira?.curadoria_analise,
        curadoria_analise_bot: item?.proposta_financeira?.curadoria_analise_bot,
        proposta_financeira: item?.proposta_financeira,
        // cnpj_integrador: item?.proposta_financeira?.integrador?.cnpj,
        // cnpj_integrador_clean:
        //   item?.proposta_financeira?.integrador?.cnpj.replace(/\D/g, ''),
        // cpf_cnpj:
        //   item?.proposta_financeira?.informacoes_cliente?.cpf ||
        //   item?.proposta_financeira?.informacoes_cliente?.cnpj,
        // cpf_cnpj_clean: item?.proposta_financeira?.informacoes_cliente?.cpf
        //   ? item?.proposta_financeira?.informacoes_cliente?.cpf.replace(
        //       /\D/g,
        //       ''
        //     )
        //   : item?.proposta_financeira?.informacoes_cliente?.cnpj.replace(
        //       /\D/g,
        //       ''
        //     ),
        cpf_cnpj: item?.cpf_cliente || '-',
        cpf_cnpj_clean:
          item?.cpf_cliente ?? item?.cpf_cliente.replace(/\D/g, ''),
      }));
  }, [registers]);

  const init = async () => {
    // filtra para mostrar os status e caso tenha alguma proposta vazia nao traz resultado.
    const result = await Read(
      '?filters[$or][0][status][$eq]=Entrega e Parecer de acesso' +
        '&filters[$or][1][status][$eq]=Instalação e Protocolo de conexão' +
        '&filters[$or][2][status][$eq]=Conexão e 1ª Parcela' +
        '&filters[$or][3][status][$eq]=Expedição' +
        '&filters[proposta_financeira][id][$notIn][0]=null'
    );
    if (result && !exposeStrapiError(result)) {
      const nresult = result.results
        ?.map((m) => {
          return { ...m };
        })
        ?.sort((a, b) => {
          // Pega a data de contato para a proposta 'a'
          const dateA = moment(a.data_entrada_conexao || a.data_enviar_conexao);
          // Pega a data de contato para a proposta 'b'
          const dateB = moment(b.data_entrada_conexao || b.data_enviar_conexao);
          // Retorna a diferença
          // Se dateA for mais antiga, o resultado é negativo e 'a' é colocado antes de 'b'.
          return dateA - dateB;
        });
      setRegisters(nresult);
    }
  };

  useEffect(() => init(), []);

  return (
    <ContainerAuthenticated>
      <TableWrapper title={'Conexão e Aviso de 1ª Parcela'} search>
        <BasicTable
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
      </TableWrapper>
    </ContainerAuthenticated>
  );
}
